<template>
  <div class="loyalty-card" :class="[loyaltyState, loyaltyData?.tierName, fullWidth ? 'full-width-card' : 'carousel-card']">
    <div class="grid-x" :class="{ 'text-center': !fullWidth }">
      <section id="loyaltyHeader" class="cell medium-3 small-12" :class="[fullWidth ? 'padding-right-l' : 'large-12']">
        <div
          v-if="loyaltyState !== LOYALTY_STATES.SOFT_SIGNED_IN"
          class="iconContainer align-middle"
          :class="{ 'align-center': !fullWidth }"
          :style="{ flexDirection: fullWidth ? 'row' : 'column' }"
        >
          <span class="tierIcon" :class="loyaltyData?.iconClass"></span>
          <span v-if="loyaltyData?.tierName === 'base'" class="loyallist-label eyebrow" :class="{ 'small-12': !fullWidth }">LOYALLIST</span>
          <span v-if="loyaltyData?.tierName === 'tol'" class="loyallist-label eyebrow" :class="{ 'small-12': !fullWidth }">TOP OF THE LIST</span>
          <span v-if="loyaltyData?.tierName === 'unl'" class="loyallist-label eyebrow" :class="{ 'small-12': !fullWidth }"
            >TOP OF THE LIST UNLOCKED</span
          >
        </div>
        <div
          v-if="loyaltyState !== LOYALTY_STATES.GUEST"
          class="tierHeader cell"
          :class="{ 'padding-top-xxs': loyaltyState !== LOYALTY_STATES.SOFT_SIGNED_IN }"
        >
          <h6 v-if="isLongName">
            {{ loyaltyData?.staticData?.longNameHeader1 + ' ' + loyaltyData?.staticData?.longNameHeader2 }}
          </h6>
          <h6 v-else>
            {{ loyaltyData?.staticData?.header + ' ' + loyaltyData?.staticData?.firstName }}
          </h6>
        </div>
      </section>
      <section class="medium-5 small-12 bodySection" :class="[fullWidth ? 'padding-right-l' : 'padding-top-xs large-12']">
        <div v-if="loyaltyState === LOYALTY_STATES.CLOSE_TO_REWARD || loyaltyState === LOYALTY_STATES.NOT_CLOSE_TO_REWARD">
          <div id="pointDetails">
            <div class="tracker">
              <ProgressBar :value="getPointsToPercentage" data-fs-mask />
              <p class="padding-left-xxs">
                <span class="h3">$25</span>
              </p>
            </div>
            <div class="progress-bar-values small">
              <div class="start-value">
                <b class="heavy-sm" data-fs-mask>{{ loyaltyData.totalAvailablePoints }}</b> / 5000 Points
              </div>
              <div class="end-value">Reward</div>
            </div>
          </div>
        </div>
        <div v-else>
          <h4 class="message">
            {{ loyaltyData?.staticData?.message }}
          </h4>
          <p v-if="loyaltyData?.staticData?.subMessage" class="subMessage body small padding-top-xxs">
            {{ loyaltyData?.staticData?.subMessage }}
          </p>
        </div>
      </section>
      <section class="medium-4 small-12 linkSection" :class="[fullWidth ? 'text-right' : 'large-12 padding-top-xs']">
        <div class="linkContainer">
          <span class="link-url">
            <a
              id="loyaltyLink"
              :href="loyaltyData?.staticData?.url"
              :data-link="loyaltyData?.staticData?.linkValue"
              :class="getLinkClass"
              @click.stop="handleLinkClickTag()"
            >
              {{ loyaltyData?.staticData?.link }}</a
            >
          </span>

          <span v-if="loyaltyState === LOYALTY_STATES.GUEST" class="link-url padding-top-xs"
            ><a
              id="signInLink"
              class="link-med"
              :href="loyaltyData?.staticData?.signInUrl"
              :data-link="loyaltyData?.staticData?.signInLinkValue"
              @click.stop="handleSignInClickTag()"
              >{{ loyaltyData?.staticData?.signInLink }}
            </a></span
          >
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ProgressBar from '@atomic-ui/progressBar/ProgressBar';
import CacheManager from '@vue-util/cacheManager';
import { Cookie, UserStatus } from '@vue-util/cookie';
import { fireTag } from '@vue-util/tagManager';
import { FETCH_REWARD_POINTS } from '@/types/actions';
import { GET_REWARD_POINTS } from '@/types/getters';
import { LOYALTY_STATES, TIER_NAMES, TIER_ICONS, TIER_HRESHOLD } from './helpers/constants';
import data from './helpers/loyaltyStaticData.json';
import { useLoyalty } from '@/stores/loyalty/loyalty';

export default {
  name: 'LoyaltyCard',
  components: {
    ProgressBar,
  },
  props: {
    fullWidth: {
      type: Boolean,
      default: () => false,
    },
  },
  setup() {
    return {
      LOYALTY_STATES,
    };
  },

  data() {
    return {
      loyaltyState: LOYALTY_STATES.GUEST,
      loyaltyData: {},
      UID: Cookie.get('bloomingdales_online_uid'),
      GUID: Cookie.get('bloomingdales_online_guid'),
      LOYALTY_DATA_CACHE_TIMEOUT: 180,
    };
  },
  computed: {
    getPointsToPercentage() {
      const points = this.loyaltyData.totalAvailablePoints;
      return (points * 100) / 5000;
    },
    isLongName() {
      const userName = this.userName();
      return !userName || userName.length > 11;
    },
    getLinkClass() {
      return this.loyaltyState === LOYALTY_STATES.GUEST ||
        this.loyaltyState === LOYALTY_STATES.NON_LOYALIST ||
        this.loyaltyState === LOYALTY_STATES.SOFT_SIGNED_IN
        ? 'primary button button-small'
        : 'link-med';
    },
  },
  async mounted() {
    const loyaltyStore = useLoyalty();
    if (UserStatus.isSignedIn()) {
      const hnfsrinfoCacheKey = `hnfsrinfo_${this.UID}`;
      if (this.GUID && Cookie.get('SSIToken', 'MISCGCs')) {
        if (!CacheManager.getPersistent(hnfsrinfoCacheKey)) {
          await loyaltyStore[FETCH_REWARD_POINTS]();
          this.loyaltyData = await loyaltyStore[GET_REWARD_POINTS];
          if (!this.loyaltyData.errors && this.loyaltyData.tierName) {
            CacheManager.setPersistent(hnfsrinfoCacheKey, this.loyaltyData, this.LOYALTY_DATA_CACHE_TIMEOUT);
            this.enableLoyaltyBanner();
          } else {
            this.enableNonLoyaltyBanner();
          }
        } else {
          this.loyaltyData = CacheManager.getPersistent(hnfsrinfoCacheKey);
          this.enableLoyaltyBanner();
        }
      } else {
        this.enableSoftSignedBanner();
      }
    } else if (UserStatus.isSoftSignedIn()) {
      this.enableSoftSignedBanner();
    } else {
      this.loyaltyData.staticData = data[this.loyaltyState];
      this.loyaltyData.iconClass = TIER_ICONS[TIER_NAMES.GUEST];
    }
  },
  methods: {
    enableNonLoyaltyBanner() {
      this.loyaltyData.staticData = data[TIER_NAMES.NON_LOYALIST];
      this.loyaltyState = LOYALTY_STATES.NON_LOYALIST;
      this.loyaltyData.iconClass = TIER_ICONS[TIER_NAMES.NON_LOYALIST];
      this.loyaltyData.staticData.firstName = this.userName();
    },
    enableSoftSignedBanner() {
      this.loyaltyData.staticData = data[TIER_NAMES.SOFT_SIGNED_IN];
      this.loyaltyState = LOYALTY_STATES.SOFT_SIGNED_IN;
      this.loyaltyData.staticData.firstName = this.userName();
    },
    enableLoyaltyBanner() {
      this.setStaticData();
    },

    setStaticData() {
      const tierName = this.tierName();
      const totalAvailablePoints = this.totalAvailablePoints();
      const rewardCardBalance = this.rewardCardBalance();
      const userName = this.userName();
      const isCloseToReward = totalAvailablePoints >= TIER_HRESHOLD.REWARD;
      const balanceAmountToUpgrade = this.balanceAmountToUpgrade();
      let isCloseToTier = false;
      if (balanceAmountToUpgrade > 0) {
        isCloseToTier =
          (tierName === TIER_NAMES.BASE && balanceAmountToUpgrade <= TIER_HRESHOLD.BASE) ||
          (tierName === TIER_NAMES.TOL && balanceAmountToUpgrade <= TIER_HRESHOLD.TOL);
      }
      let staticData;
      if (tierName === TIER_NAMES.DEFAULT) {
        staticData = data[tierName];
      } else if (rewardCardBalance > 0) {
        // has rewards
        staticData = data[tierName].signedInHasRewardsMoney;
        staticData.message = staticData.message.replace('::rewardCardBalance', this.loyaltyData.rewardCardBalance);
      } else if (isCloseToTier) {
        // close to next tier: has points and no rewards money
        staticData = data[tierName].signedInHasNoRewardsMoney.closeToNextTier;
        staticData.message = staticData.message.replace('::balanceAmountToUpgrade', this.loyaltyData.balanceAmountToUpgrade.replace(/\.00$/, ''));
      } else if (isCloseToReward) {
        // close to next reward: has points and no rewards money
        staticData = data[tierName].signedInHasNoRewardsMoney.closeToReward;
      } else if (totalAvailablePoints > 0) {
        // not close to next reward
        staticData = data[tierName].signedInHasNoRewardsMoney.notCloseToReward;
      } else {
        // has no rewards, no points
        staticData = data[tierName].noPointsNoRewards;
      }

      staticData.firstName =
        staticData.state === LOYALTY_STATES.CLOSE_TO_REWARD || staticData.state === LOYALTY_STATES.CLOSE_TO_NEXT_TIER
          ? `${userName}!`
          : `${userName}`;

      // set the staticdata and userName along with loyalty details
      this.loyaltyData.rewardCardBalance = rewardCardBalance;
      this.loyaltyData.totalAvailablePoints = totalAvailablePoints;
      this.loyaltyData.totalAvailablePointsWithComma = totalAvailablePoints.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      this.loyaltyData.tierName = tierName;
      this.loyaltyData.staticData = staticData;
      this.loyaltyData.iconClass = TIER_ICONS[tierName];
      this.loyaltyState = staticData.state;
    },

    userName() {
      return Cookie.get('UserName', 'MISCGCs') || '';
    },

    tierName() {
      const fullTierName = String(this.loyaltyData.tierName).toLowerCase() || '';
      let tierName;

      if (fullTierName.includes(TIER_NAMES.BASE)) {
        tierName = TIER_NAMES.BASE;
      } else if (fullTierName.includes(TIER_NAMES.TOL)) {
        tierName = TIER_NAMES.TOL;
      } else if (fullTierName.includes(TIER_NAMES.UNL)) {
        tierName = TIER_NAMES.UNL;
      } else {
        tierName = TIER_NAMES.DEFAULT;
      }
      return tierName;
    },

    totalAvailablePoints() {
      const totalAvailablePoints = parseInt((this.loyaltyData.totalAvailablePoints || '').replace(',', ''), 10);
      return Number.isNaN(totalAvailablePoints) ? 0 : totalAvailablePoints;
    },

    balanceAmountToUpgrade() {
      const balanceAmountToUpgrade = parseFloat((this.loyaltyData.balanceAmountToUpgrade || '').replace(/[$,]/g, ''), 10);
      return Number.isNaN(balanceAmountToUpgrade) ? 0 : balanceAmountToUpgrade;
    },

    rewardCardBalance() {
      const rcBalance = this.loyaltyData.rewardCardBalance || '0';
      return parseFloat(rcBalance.replace('$', ''), 10);
    },

    handleLinkClickTag() {
      const linkName = this.loyaltyData.staticData.linkValue;
      fireTag('link', { link_name: linkName, event_name: 'hp dash click' });
    },
    handleSignInClickTag() {
      fireTag('link', { link_name: 'hp_dash-loyalty signup', event_name: 'hp dash click' });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@core/csg/src/scss/encodedIcons/bcom/_loyallist-large.scss';
@import '~@core/csg/src/scss/encodedIcons/bcom/_loyallist-large-black.scss';
@import '~@core/csg/src/scss/encodedIcons/bcom/_loyallist-key-large.scss';

.cell {
  & > .loyalty-card {
    width: calc(200% + 8px);
    height: 100%;
    margin-bottom: 0;
    justify-content: center;

    @include breakpoint(large) {
      width: calc(200% + 24px);
    }
  }
}

.loyalty-card {
  width: 100%; //keep this
  margin-bottom: rem-calc(32px);
  background-color: $background-color;
  display: flex;
  flex-direction: column;
  gap: rem-calc(16px);

  ::v-deep(.p-progressbar) {
    border: none;
    height: rem-calc(6px);
  }

  .loyallist-label {
    color: $gray-1-color;
    display: block;
    padding-top: rem-calc(5px);
  }
}

.iconContainer {
  display: flex;

  .tierIcon {
    width: rem-calc(28px);
    height: rem-calc(28px);
    background-size: rem-calc(28px) rem-calc(28px);
  }
}

.tracker {
  display: flex;
  align-items: center;
  width: 100%;

  .progress-bar {
    width: inherit;
  }
}

.base {
  ::v-deep(.p-progressbar) {
    .p-progressbar-value {
      background-color: $loyalty-1-color;
    }
  }
}

.tol {
  ::v-deep(.p-progressbar) {
    .p-progressbar-value {
      background-color: $loyalty-2-color;
    }
  }
}

.unl {
  ::v-deep(.p-progressbar) {
    .p-progressbar-value {
      background-color: $loyalty-4-color;
    }
  }
}

.progress-bar-values {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  .end-value {
    white-space: nowrap;
  }
}

.linkSection {
  display: flex;
}

.linkContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.carousel-card {
  padding: rem-calc(24px) rem-calc(20px);

  section {
    width: 100%;
  }

  @include breakpoint(medium) {
    padding: rem-calc(16px) rem-calc(20px);
  }

  @include breakpoint(large) {
    padding: rem-calc(16px) 14.5%;
  }

  .linkSection {
    justify-content: center;
  }
}

@include breakpoint(medium) {
  .full-width-card {
    padding: rem-calc(24px) rem-calc(40px);

    #loyaltyHeader {
      width: 28%;

      .loyallist-label {
        padding-top: 0;
        padding-left: rem-calc(4px);
      }
    }

    .bodySection {
      width: calc(72% - 110px);
    }

    .linkSection {
      width: fit-content;
      justify-content: end;
      align-items: center;
    }
  }

  .guest.full-width-card {
    #loyaltyHeader {
      width: rem-calc(80px);
    }

    .bodySection {
      width: calc(100% - 271px);
    }
  }

  .Soft_SignedIn,
  .nonLoyalist {
    &.full-width-card {
      #loyaltyHeader {
        width: 25%;
      }
    }
  }

  .nonLoyalist.full-width-card {
    .bodySection {
      width: calc(75% - 191px);
    }
  }

  .Soft_SignedIn.full-width-card {
    .bodySection {
      width: calc(75% - 90px);
    }
  }
}

@include breakpoint(small only) {
  .full-width-card {
    text-align: center;
    padding: rem-calc(24px) rem-calc(20px);

    #loyaltyHeader {
      padding-right: 0 !important;

      .iconContainer {
        justify-content: center;
        flex-direction: column !important;

        .loyallist-label {
          padding-top: rem-calc(8px);
        }
      }
    }

    .bodySection {
      padding: rem-calc(16) 0 !important;
    }

    .linkSection {
      justify-content: center;
    }
  }
}

.guest,
.Soft_SignedIn,
.nonLoyalist {
  &.full-width-card {
    .linkSection {
      align-items: flex-start;
    }
  }

  .tierIcon {
    width: rem-calc(40px);
    height: rem-calc(40px);
    background-size: rem-calc(40px) rem-calc(40px);
  }
}
</style>
